
import {
  defineComponent,
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  watch,
} from "vue";
import { useQuery } from "@vue/apollo-composable";

import TimerSuggestionBubble from "@/components/TimerSuggestionBubble.vue";
import FloatingTimer from "@/components/FloatingTimer.vue";

import { GET_USER } from "@/graphql/user/queries";
import { isEmployee } from "@/helpers/router/queries";
import { useTimerAssistant } from "@/composables/useTimerAssistant";

export default defineComponent({
  components: {
    TimerSuggestionBubble,
    FloatingTimer,
  },
  setup() {
    const suggestionBubble = ref<InstanceType<
      typeof TimerSuggestionBubble
    > | null>(null);
    const floatingTimerRef = ref<InstanceType<typeof FloatingTimer> | null>(
      null,
    );
    const suggestionMessage = ref(
      "No active timer. Want to start tracking your time?",
    );
    const suggestionButtonText = ref("Start Timer");
    const suggestionIcon = ref("material-symbols:timer");
    const showSuggestion = ref(true);

    const { result: userResult } = useQuery(GET_USER);
    const user = computed(() => userResult.value?.user);
    const userId = computed(() => user.value?.id ?? "");
    const isUserEmployee = computed(() => isEmployee(user.value?.accessRights));

    const { currentTimer } = useTimerAssistant({
      userId,
      isEmployee: isUserEmployee,
    });

    // Hide suggestion if there's already a timer (running or paused) after page reload
    watch(
      currentTimer,
      (timer) => {
        if (timer) {
          showSuggestion.value = false;
        }
      },
      { immediate: true },
    );

    // Listen for timer started event from FloatingTimer
    const handleTimerStarted = () => {
      showSuggestion.value = false;
    };

    const handleSuggestionAction = () => {
      // Trigger the theme selector in FloatingTimer
      floatingTimerRef.value?.openThemeSelector();
    };

    const dismissSuggestion = () => {
      showSuggestion.value = false;
    };

    onMounted(() => {
      setTimeout(() => {
        // Only show if there's no timer at all
        if (!currentTimer.value) {
          suggestionBubble.value?.show();
        }
      }, 1000);

      // Listen for timer started event
      window.addEventListener("fides:timerStarted", handleTimerStarted);
    });

    onBeforeUnmount(() => {
      window.removeEventListener("fides:timerStarted", handleTimerStarted);
    });

    return {
      suggestionBubble,
      floatingTimerRef,
      suggestionMessage,
      suggestionButtonText,
      suggestionIcon,
      userId,
      isUserEmployee,
      currentTimer,
      showSuggestion,
      handleSuggestionAction,
      dismissSuggestion,
    };
  },
});
